"use client";
import { useRouter } from "next/navigation";
import { useId, useState } from "react";
import { durationOptions, vibeOptions } from "@/app/lib/tripOptions";

// Component for creating a trip
function ItineraryCreationForm({ autoFocus = false }: { autoFocus?: boolean }) {
  const [city, setCity] = useState("");
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const [duration, setDuration] = useState<string | null>(null);
  const [startDate, setStartDate] = useState("");
  const [vibes, setVibes] = useState<string[]>([]);
  const router = useRouter();
  const optionsId = useId();
  const startDateId = useId();
  const todayIso = new Date().toISOString().split("T")[0];

  const toggleVibe = (value: string) => {
    setVibes((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!city.trim()) return;

    // Use standardized URL for base/unmodified itineraries
    const formattedCity = city.toLowerCase().trim();

    // Everything below is opt-in — if the visitor never opens "Customize",
    // this produces the exact same /show/{city} URL as before.
    const params = new URLSearchParams();
    if (duration) params.set("days", duration);
    if (startDate) params.set("start", startDate);
    if (vibes.length) params.set("vibe", vibes.join(","));
    const queryString = params.toString();

    router.push(`/show/${formattedCity}${queryString ? `?${queryString}` : ""}`);
  };

  return (
    <div className="w-full max-w-xl mx-auto">
      <form
        name="HomePageForm"
        className="flex flex-col gap-4 w-full"
        onSubmit={handleSubmit}
      >
        <div
          className={`relative w-full bg-white/10 border-2 ${
            isInputFocused
              ? "border-white/50 bg-white/20 shadow-lg"
              : "border-white/20"
          } rounded-xl transition-all duration-300 backdrop-blur-md`}
        >
          <svg
            className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-white"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="11" cy="11" r="8"></circle>
            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
          </svg>
          <input
            autoFocus={autoFocus}
            placeholder="Where do you want to go?"
            aria-label="Destination"
            name="city"
            className="w-full bg-transparent border-none py-4 px-12 text-lg text-white placeholder-white/70 outline-none font-sans"
            value={city}
            onChange={(evt) => {
              const valueToSet = evt.target.value.replace(/[^a-z ]/gi, "");
              setCity(valueToSet);
            }}
            onFocus={() => setIsInputFocused(true)}
            onBlur={() => setIsInputFocused(false)}
          />
        </div>

        <button
          type="button"
          onClick={() => setShowOptions((prev) => !prev)}
          aria-expanded={showOptions}
          aria-controls={optionsId}
          className="self-start flex items-center gap-1.5 text-sm text-white/75 hover:text-white transition-colors cursor-pointer"
        >
          <svg
            className={`w-4 h-4 transition-transform ${
              showOptions ? "rotate-180" : ""
            }`}
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <polyline points="6 9 12 15 18 9"></polyline>
          </svg>
          {showOptions ? "Hide trip details" : "Customize your trip (optional)"}
        </button>

        <div
          id={optionsId}
          className={`grid transition-all duration-300 ease-in-out ${
            showOptions ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
          }`}
        >
          <div className="overflow-hidden">
            <div className="flex flex-col gap-4 pb-1 text-left">
              <div>
                <p className="text-xs font-medium text-white/70 mb-2">
                  Trip length
                </p>
                <div className="flex flex-wrap gap-2">
                  {durationOptions.map((option) => {
                    const selected = duration === option.value;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() =>
                          setDuration((prev) =>
                            prev === option.value ? null : option.value
                          )
                        }
                        aria-pressed={selected}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors cursor-pointer ${
                          selected
                            ? "bg-gradient-to-r from-orange-500 to-amber-400 border-transparent text-white"
                            : "bg-white/10 border-white/20 text-white/85 hover:bg-white/20"
                        }`}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label
                  htmlFor={startDateId}
                  className="text-xs font-medium text-white/70 mb-2 block"
                >
                  Start date
                </label>
                <input
                  id={startDateId}
                  type="date"
                  min={todayIso}
                  value={startDate}
                  onChange={(event) => setStartDate(event.target.value)}
                  className="bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-sm text-white [color-scheme:dark] focus:outline-none focus:ring-2 focus:ring-white/40"
                />
              </div>

              <div>
                <p className="text-xs font-medium text-white/70 mb-2">
                  Trip vibe
                </p>
                <div className="flex flex-wrap gap-2">
                  {vibeOptions.map((option) => {
                    const selected = vibes.includes(option.value);
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => toggleVibe(option.value)}
                        aria-pressed={selected}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors cursor-pointer ${
                          selected
                            ? "bg-gradient-to-r from-orange-500 to-amber-400 border-transparent text-white"
                            : "bg-white/10 border-white/20 text-white/85 hover:bg-white/20"
                        }`}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>

        <button
          className={`flex items-center justify-center gap-2 cursor-pointer disabled:cursor-not-allowed ${
            city.trim()
              ? "bg-gradient-to-r from-orange-500 to-amber-400 hover:from-orange-600 hover:to-amber-500 transform hover:-translate-y-1"
              : "bg-gray-400 cursor-not-allowed"
          } text-white border-none rounded-xl py-4 px-6 text-lg font-semibold transition-all duration-300 shadow-md hover:shadow-lg`}
          type="submit"
          disabled={!city.trim()}
        >
          Plan My Trip
          <svg
            className="w-5 h-5"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="5" y1="12" x2="19" y2="12"></line>
            <polyline points="12 5 19 12 12 19"></polyline>
          </svg>
        </button>
      </form>
    </div>
  );
}

export default ItineraryCreationForm;
